import { useEffect, useRef, useState, useCallback } from "react";
import { X, ScanLine, Check, Camera, RefreshCcw, ImagePlus } from "lucide-react";
import { HitRing } from "@/components/loading";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * Escáner de cartas con IA — abre la cámara del dispositivo y manda frames
 * a `/api/scan/card` cada ~1.2 s. Gemini Flash lee el nombre + número, y el
 * backend busca la carta en TCGdex (Pokémon) o RiftScribe (Riftbound).
 *
 * Modos:
 *   - AUTO: loop de cámara que dispara solo (rápido, más estricto).
 *   - MANUAL: el usuario toca "Escanear ahora" y capturamos el frame actual
 *     con `manual=true`. El backend devuelve TODOS los candidatos con %
 *     aunque la detección sea débil — el usuario elige.
 *   - GALLERY: el usuario sube una foto del dispositivo (`manual=true`).
 *
 * props:
 *   open: bool
 *   onClose: () => void
 *   game: "pokemon" | "riftbound"
 *   onCardDetected: (card, detected) => void  // cuando el usuario confirma
 */
const SCAN_INTERVAL_MS = 900;
const JPEG_QUALITY = 0.6;

const GAME_LABEL = { pokemon: "Pokémon", riftbound: "Riftbound" };

export function CardScanner({ open, onClose, game, onCardDetected }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const scanningRef = useRef(false);         // evita superponer llamadas
  const timerRef = useRef(null);
  const attemptRef = useRef(0);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState("starting"); // starting | scanning | analyzing | detected | manual_pick | error | limit
  const [hint, setHint] = useState("Centra la carta en el cuadro");
  const [stage, setStage] = useState(null);
  const [lastSeen, setLastSeen] = useState(null);
  const [detected, setDetected] = useState(null);
  const [alternatives, setAlternatives] = useState([]);
  const [candidates, setCandidates] = useState([]); // lista para modo manual
  const [manualMode, setManualMode] = useState(false);
  const [cameraErr, setCameraErr] = useState(null);
  const [busyManual, setBusyManual] = useState(false);

  const gameLabel = GAME_LABEL[game] || "Pokémon";

  const stopLoop = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    scanningRef.current = false;
  }, []);

  const stopCamera = useCallback(() => {
    const stream = videoRef.current?.srcObject;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    stopLoop();
  }, [stopLoop]);

  useEffect(() => {
    if (!open) { stopCamera(); return; }
    let cancelled = false;
    // Reset state al abrir o cambiar de juego
    attemptRef.current = 0;
    setAttempt(0);
    setStage(null);
    setLastSeen(null);
    setDetected(null);
    setAlternatives([]);
    setCandidates([]);
    setManualMode(false);
    setBusyManual(false);
    setHint(`Centra la carta ${gameLabel} en el cuadro`);
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setStatus("scanning");
        timerRef.current = setInterval(captureAndScan, SCAN_INTERVAL_MS);
      } catch (e) {
        setCameraErr(e?.message || "No se pudo acceder a la cámara");
        setStatus("error");
      }
    })();
    return () => { cancelled = true; stopCamera(); };
  }, [open, game]);

  const hintForStage = (data) => {
    const s = data?.stage;
    const detName = data?.detected?.name || data?.detected?.number;
    if (s === "no_text") return "No pude leer texto claro. Mejora la luz y acerca la carta.";
    if (s === "no_card") return "No veo una carta. Céntrala dentro del marco.";
    if (s === "low_confidence") {
      return detName
        ? `Detección débil de "${detName}". Acerca más la carta.`
        : "Detección débil. Acerca más la carta.";
    }
    if (s === "not_found") {
      return data?.hint
        || `Detecté "${detName}" pero no está en el catálogo de ${gameLabel}. Reintentando…`;
    }
    return data?.hint || "Ajusta la posición";
  };

  // Grab a JPEG frame from the <video> element and return base64.
  const grabFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) return null;
    const targetW = 640;
    const scale = targetW / video.videoWidth;
    canvas.width = targetW;
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  };

  const captureAndScan = async () => {
    if (scanningRef.current || !videoRef.current) return;
    scanningRef.current = true;
    attemptRef.current += 1;
    setAttempt(attemptRef.current);
    setStatus("analyzing");
    setHint(`Analizando frame ${attemptRef.current} en ${gameLabel}…`);
    try {
      const dataUrl = grabFrame();
      if (!dataUrl) return;
      const r = await api.post("/scan/card", { image_base64: dataUrl, game });
      const d = r.data || {};
      if (d.ok && d.card) {
        stopLoop();
        setStatus("detected");
        setStage("matched");
        setManualMode(false);
        setDetected({ card: d.card, meta: d.detected, confidence: d.confidence });
        setAlternatives(d.alternatives || []);
        setCandidates(d.candidates || [d.card, ...(d.alternatives || [])]);
        setHint("");
      } else {
        setStatus("scanning");
        setStage(d.stage || null);
        const detName = d.detected?.name || d.detected?.number;
        if (detName) setLastSeen(detName);
        setHint(hintForStage(d));
      }
    } catch (e) {
      const s = e?.response?.status;
      if (s === 429) {
        stopLoop();
        setStatus("limit");
        setStage("limit");
        setHint(e?.response?.data?.detail || "Alcanzaste el límite diario de escaneos.");
      } else if (s === 401) {
        setStatus("error");
        setStage("auth");
        setHint("Inicia sesión para usar el escáner.");
      } else if (s === 502) {
        setStatus("scanning");
        setStage("service_error");
        setHint("El servicio de IA está lento. Reintentando…");
      } else {
        setStatus("scanning");
        setStage("network");
        setHint("Sin conexión al servidor. Reintentando…");
      }
    } finally {
      scanningRef.current = false;
    }
  };

  const uploadFromGallery = async (file) => {
    if (!file || busyManual) return;
    setBusyManual(true);
    stopLoop();
    setStatus("analyzing");
    setHint(`Analizando foto para ${gameLabel}…`);
    try {
      // Read file → downscale to ≤900px → base64 JPEG
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
          const img = new Image();
          img.onerror = reject;
          img.onload = () => {
            const maxW = 900;
            const scale = Math.min(1, maxW / img.width);
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/jpeg", 0.75));
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
      });
      const r = await api.post("/scan/card", { image_base64: dataUrl, game, manual: true });
      handleManualResponse(r.data || {});
    } catch (e) {
      const s = e?.response?.status;
      setStatus("scanning");
      if (s === 429) {
        setStatus("limit"); setStage("limit");
        setHint(e?.response?.data?.detail || "Alcanzaste el límite diario de escaneos.");
      } else {
        setHint("No pudimos analizar la imagen. Prueba con otra foto.");
      }
    } finally {
      setBusyManual(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleManualResponse = (d) => {
    const detName = d.detected?.name || d.detected?.number;
    if (detName) setLastSeen(detName);
    const list = d.candidates || (d.card ? [d.card, ...(d.alternatives || [])] : []);
    if (list.length > 0) {
      // Modo manual: mostramos panel con TODOS los candidatos, aunque conf sea baja.
      setStatus("manual_pick");
      setStage(d.stage || "manual_pick");
      setManualMode(true);
      setDetected({ card: list[0], meta: d.detected, confidence: d.confidence });
      setAlternatives(list.slice(1));
      setCandidates(list);
      setHint("");
    } else {
      // Nada encontrado en catálogo.
      setStatus("scanning");
      setStage(d.stage || "not_found");
      setHint(hintForStage(d));
    }
  };

  const restartScan = () => {
    setDetected(null);
    setAlternatives([]);
    setCandidates([]);
    setManualMode(false);
    setStatus("scanning");
    setStage(null);
    attemptRef.current = 0;
    setAttempt(0);
    setHint(`Centra la carta ${gameLabel} en el cuadro`);
    if (!timerRef.current) timerRef.current = setInterval(captureAndScan, SCAN_INTERVAL_MS);
  };

  const acceptCard = (card) => {
    onCardDetected(card, detected?.meta || null);
    onClose();
  };

  if (!open) return null;

  const frameCls = (() => {
    if (status === "detected" || status === "manual_pick") return "border-emerald-400";
    if (status === "analyzing") return "border-cyan-400 animate-pulse";
    if (status === "error" || status === "limit") return "border-red-400";
    if (stage === "not_found") return "border-amber-400 animate-pulse";
    if (stage === "low_confidence") return "border-yellow-400 animate-pulse";
    return "border-white/40 animate-pulse";
  })();

  const showSpinner = status === "analyzing" || (stage === "not_found" && !detected) || (stage === "low_confidence" && !detected);
  const showResultPanel = (status === "detected" || status === "manual_pick") && detected?.card;
  const confidencePct = Math.round((detected?.confidence || 0) * 100);

  return (
    <div className="fixed inset-0 z-[80] bg-black/95 flex flex-col" data-testid="card-scanner-modal">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626]">
        <div className="flex items-center gap-2 min-w-0">
          <ScanLine size={18} className="text-[#EF4444] shrink-0" />
          <div className="font-display text-sm sm:text-base truncate">Escanear carta</div>
          <span
            data-testid="card-scanner-game-badge"
            className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider border ${
              game === "riftbound"
                ? "bg-blue-500/10 text-blue-300 border-blue-500/40"
                : "bg-yellow-500/10 text-yellow-300 border-yellow-500/40"
            }`}
          >
            {gameLabel}
          </span>
        </div>
        <button data-testid="card-scanner-close" onClick={onClose} className="p-2 hover:bg-[#171717] rounded">
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 relative overflow-hidden bg-black">
        <video
          ref={videoRef}
          playsInline
          muted
          className="w-full h-full object-contain"
          data-testid="card-scanner-video"
        />
        <canvas ref={canvasRef} className="hidden" />

        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className={`w-64 h-96 sm:w-80 sm:h-[28rem] border-4 rounded-2xl transition-colors ${frameCls}`}>
            {(status === "detected" || status === "manual_pick") && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-emerald-500 rounded-full p-4 shadow-lg" data-testid="scan-check">
                  <Check size={48} className="text-white" strokeWidth={3} />
                </div>
              </div>
            )}
            {status === "error" && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-red-500 rounded-full p-4 shadow-lg">
                  <X size={48} className="text-white" strokeWidth={3} />
                </div>
              </div>
            )}
          </div>
        </div>

        {hint && !showResultPanel && (
          <div
            className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-black/85 border border-[#262626] px-4 py-2 rounded-full text-sm max-w-[92%] text-center flex items-center gap-2"
            data-testid="scan-hint"
          >
            {showSpinner && <HitRing tone="light" size={14} />}
            <span className="truncate">{hint}</span>
          </div>
        )}

        {(attempt > 0 && status !== "detected" && status !== "manual_pick") && (
          <div className="absolute top-3 left-3 bg-black/70 border border-[#262626] px-2.5 py-1 rounded text-[11px] text-neutral-300 flex items-center gap-2" data-testid="scan-attempt">
            <span className="text-cyan-400">Intento {attempt}</span>
            {lastSeen && <span className="text-neutral-500 truncate max-w-[10rem]">· vio &quot;{lastSeen}&quot;</span>}
          </div>
        )}

        {/* Barra de acciones — botón de galería siempre visible mientras la
             cámara está activa. Da al usuario un escape confiable cuando el
             auto-loop tarda o el ambiente tiene mucha luz. */}
        {!cameraErr && !showResultPanel && status !== "limit" && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2" data-testid="scan-manual-bar">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={busyManual}
              data-testid="scan-gallery-btn"
              className="bg-black/70 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 h-11 px-5"
              title="Sube una foto desde tu galería si la cámara no la reconoce"
            >
              {busyManual ? <span className="mr-1.5 inline-flex"><HitRing tone="light" size={14} /></span> : <ImagePlus size={16} className="mr-1.5" />}
              Cargar desde galería
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="scan-gallery-input"
              onChange={(e) => { if (e.target.files?.[0]) uploadFromGallery(e.target.files[0]); }}
            />
          </div>
        )}

        {cameraErr && (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
            <div className="bg-[#171717] border border-[#262626] rounded-md p-6 max-w-md">
              <Camera size={32} className="text-neutral-500 mx-auto mb-3" />
              <div className="font-display text-base mb-2">No pudimos acceder a la cámara</div>
              <div className="text-sm text-neutral-400 mb-4">{cameraErr}</div>
              <div className="text-xs text-neutral-500 mb-3">Aún puedes subir una foto desde tu galería.</div>
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={busyManual}
                data-testid="scan-gallery-btn-fallback"
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                <ImagePlus size={14} className="mr-1.5" /> Cargar desde galería
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) uploadFromGallery(e.target.files[0]); }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Panel de confirmación — modo auto (1 carta + alternativas) o modo
           manual (lista de candidatos con % para elegir). */}
      {showResultPanel && (
        <div className="border-t border-[#262626] bg-[#0F0F0F] p-4 max-h-[45vh] overflow-y-auto" data-testid="scan-result-panel">
          <div className="text-xs text-neutral-500 uppercase tracking-wider mb-2 flex items-center justify-between gap-2">
            <span>
              {manualMode ? "Elige la carta correcta" : `Detectado (${confidencePct}%)`}
              {detected?.meta?.name && <span className="text-neutral-400 normal-case tracking-normal"> · leí &quot;{detected.meta.name}&quot;</span>}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={restartScan}
              data-testid="scan-retry"
              className="h-7 text-neutral-400 hover:text-white"
            >
              <RefreshCcw size={12} className="mr-1" /> Reintentar
            </Button>
          </div>

          {manualMode ? (
            // Modo manual: lista completa de candidatos, cada uno pickeable.
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="scan-candidates">
              {candidates.map((c, i) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`scan-candidate-${c.id}`}
                  onClick={() => acceptCard(c)}
                  className="text-left p-2 border border-[#262626] rounded-md bg-[#0A0A0A] hover:border-emerald-500 hover:bg-emerald-500/5 transition-colors"
                >
                  <div className="aspect-[3/4] bg-[#111] rounded-sm overflow-hidden mb-1 flex items-center justify-center relative">
                    {c.image_url
                      ? <img src={c.image_url} alt={c.name} className="w-full h-full object-contain" />
                      : <span className="text-[10px] text-neutral-600">Sin imagen</span>}
                    {i === 0 && confidencePct > 0 && (
                      <span className="absolute top-1 right-1 bg-emerald-500/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                        {confidencePct}%
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] line-clamp-2 font-medium">{c.name}</div>
                  <div className="text-[10px] text-neutral-500 line-clamp-1">{c.set_name || "—"}</div>
                  <div className="text-[10px] text-[#EF4444] font-mono">{c.local_id || ""}</div>
                </button>
              ))}
            </div>
          ) : (
            // Modo auto: 1 tarjeta principal + alternativas horizontales.
            <>
              <div className="flex items-start gap-3">
                {detected.card.image_url && (
                  <img src={detected.card.image_url} alt="" className="w-16 h-24 object-contain rounded bg-[#171717] shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-white truncate">{detected.card.name}</div>
                  <div className="text-xs text-neutral-400 truncate">{detected.card.set_name} · {detected.card.local_id}</div>
                  {detected.meta?.language && detected.meta.language !== "es" && (
                    <div className="text-[10px] text-neutral-500 mt-1">Idioma detectado: {detected.meta.language.toUpperCase()}</div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  type="button"
                  data-testid="scan-confirm-primary"
                  onClick={() => acceptCard(detected.card)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  <Check size={16} className="mr-1" /> Sí, es esta
                </Button>
              </div>
              {alternatives.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">¿Es otra variante?</div>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {alternatives.map((c) => (
                      <button
                        key={c.id}
                        data-testid={`scan-alt-${c.id}`}
                        onClick={() => acceptCard(c)}
                        className="shrink-0 border border-[#262626] rounded p-1 hover:border-emerald-500 bg-[#111]"
                      >
                        {c.image_url && <img src={c.image_url} alt="" className="w-12 h-16 object-contain" />}
                        <div className="text-[9px] text-neutral-400 mt-1 max-w-[3rem] truncate">{c.local_id}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CardScanner;
